import { type HTMLAttributes, type ReactNode } from "react";
import styled, { css } from "styled-components";

import { mediaViewportFitCss } from "../foundations/mediaViewportFit";
import { MediaFrameActions } from "./MediaFrameActions";
import { mediaFullscreenButtonRevealCss } from "./mediaFullscreenButtonReveal";

const Viewport = styled.div<{
  $aspectRatio?: number;
  $bordered: boolean;
  $maxHeight?: string;
}>`
  position: relative;
  box-sizing: border-box;
  ${({ $aspectRatio, $maxHeight }) =>
    $aspectRatio !== undefined && $maxHeight !== undefined
      ? mediaViewportFitCss({ aspectRatio: $aspectRatio, maxHeight: $maxHeight })
      : css`
          width: 100%;
          max-width: 100%;
          max-height: ${$maxHeight ?? "none"};
          aspect-ratio: ${$aspectRatio ?? "auto"};
        `}
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
  border: ${({ theme, $bordered }) =>
    $bordered ? `1px solid ${theme.color.border.default}` : "0"};
  border-radius: ${({ theme }) => theme.shape.radius.surface};

  ${mediaFullscreenButtonRevealCss}
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

export interface MediaFrameProps extends HTMLAttributes<HTMLDivElement> {
  /** media element(img/canvas/svg/video). 프레임을 채우는 방식(width/object-fit)은 child 책임. */
  children: ReactNode;
  /** width/height 비율. 생략하면 콘텐츠가 자기 크기를 정한다(이미지 natural 크기 등). */
  aspectRatio?: number;
  /** 프레임 테두리. Default true. */
  bordered?: boolean;
  /**
   * 프레임 높이 cap(CSS length). aspectRatio와 함께 주면 child intrinsic size와 무관하게
   * 컨테이너와 높이 cap 중 작은 fill target까지 폭을 확대·축소하며 비율을 보존한다. 이때
   * 부모보다 좁아질 수 있으므로 inline-axis 정렬은 부모 wrapper가 소유한다.
   */
  maxHeight?: string;
  /** media 위 장식층 — pointer-events: none (blank/mask/가이드 등). */
  overlay?: ReactNode;
  /** 우상단 인터랙티브 액션 row — `MediaFrameActions`로 감싸 렌더. */
  actions?: ReactNode;
}

/**
 * 사진·캔버스·비디오가 공유하는 미디어 프레임(시각적 틀): aspect-ratio viewport +
 * recessed 배경 + 테두리 + `overlay`(장식층)/`actions`(인터랙티브 row) 슬롯.
 * `VideoFrame`은 이 위에 native `<video>` 엔진만 얹는 특수화다. fullscreen 열림
 * 상태·zoom은 여기 없다 — consumer + `MediaOverlay`/`ZoomPanWrapper` composition
 * (RFC-media-frame §경계).
 */
export function MediaFrame({
  children,
  aspectRatio,
  bordered = true,
  maxHeight,
  overlay,
  actions,
  ...rest
}: MediaFrameProps) {
  return (
    <Viewport $aspectRatio={aspectRatio} $bordered={bordered} $maxHeight={maxHeight} {...rest}>
      {children}
      {overlay ? <Overlay>{overlay}</Overlay> : null}
      {actions ? <MediaFrameActions>{actions}</MediaFrameActions> : null}
    </Viewport>
  );
}

MediaFrame.displayName = "MediaFrame";
